// Sales Machine: Monada's own outreach (super-admin). Five tabs: build an audience from Apollo,
// instruct and review the messages, launch the campaign, see the replies, connect the mailbox.
// Server: src/server/api/salesMachine.js. Plan and log: docs/sales-machine/README.md.

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useOutletContext } from 'react-router-dom';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Alert from '@mui/material/Alert';

import AudienceTab from './salesMachine/AudienceTab.js';
import ContentTab from './salesMachine/ContentTab.js';
import LaunchTab from './salesMachine/LaunchTab.js';
import StatsTab from './salesMachine/StatsTab.js';
import SettingsTab from './salesMachine/SettingsTab.js';
import { smGet, errorText, setToken } from './salesMachine/api.js';

const TABS = [
    { key: 'audience', label: '1. Audience' },
    { key: 'content', label: '2. Content' },
    { key: 'launch', label: '3. Launch' },
    { key: 'stats', label: '4. Statistics' },
    { key: 'settings', label: 'Settings' },
];

function SalesMachine() {
    const { accesstoken } = useOutletContext();
    setToken(accesstoken);
    const [params, setParams] = useSearchParams();
    const tab = TABS.some(t => t.key === params.get('tab')) ? params.get('tab') : 'audience';
    const campaignId = params.get('campaign') || '';
    const [settings, setSettings] = useState(null);
    const [error, setError] = useState(null);

    const go = useCallback((nextTab, nextCampaign = campaignId) => {
        const p = { tab: nextTab };
        if (nextCampaign) p.campaign = nextCampaign;
        setParams(p);
    }, [campaignId, setParams]);

    const loadSettings = useCallback(async () => {
        try {
            setSettings(await smGet('/settings'));
        } catch (e) {
            setError(errorText(e));
        }
    }, []);
    useEffect(() => { loadSettings(); }, [loadSettings]);

    return (
        <Box sx={{ p: 3, maxWidth: 1400 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>Sales Machine</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Build an audience, write and approve the messages, launch the campaign and follow the replies.
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {settings && !settings.mailbox?.connected && tab !== 'settings' && (
                <Alert severity="info" sx={{ mb: 2 }}>No mailbox is connected yet. Connect one in Settings before launching a campaign.</Alert>
            )}
            <Tabs value={tab} onChange={(e, v) => go(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                {TABS.map(t => <Tab key={t.key} value={t.key} label={t.label} />)}
            </Tabs>
            {tab === 'audience' && <AudienceTab settings={settings} onCampaignCreated={id => go('content', id)} />}
            {tab === 'content' && <ContentTab campaignId={campaignId} onCampaign={id => go('content', id)} onLaunch={() => go('launch')} />}
            {tab === 'launch' && <LaunchTab campaignId={campaignId} settings={settings} onCampaign={id => go('launch', id)} />}
            {tab === 'stats' && <StatsTab campaignId={campaignId} onCampaign={id => go('stats', id)} />}
            {tab === 'settings' && <SettingsTab settings={settings} onSaved={setSettings} />}
        </Box>
    );
}

export default SalesMachine;
